import {Flex, HTMLDialog} from '@priority-software/priority-style-react';
import React, {useCallback, useEffect, useMemo} from 'react';
import {ReportsFooter} from './ReportsFooter/ReportsFooter';
import {ReportsToolbarContent} from './ReportsToolbarContent/ReportsToolbarContent';
import {ReportsContent} from './ReportsContent/ReportsContent';
import {CircularLoader} from '../../../components/CircularLoader/CircularLoader';
import {analyticsReportsStore} from '../store/AnalyticsReportsStore';
import {useReportQuery} from '../../ReportGenerator/useReportsQuery';
import texts from '../../ReportGenerator/texts';
import {ADD_ANALYTICS_TO_MENU, ANALYTICS_REPORT_DIALOG} from '../constants';
import styles from './styles.module.scss';
import {observer} from 'mobx-react-lite';
import {EditField} from 'priority-web-sdk';
import {runProcedure} from '../actions/proc';
import {reloadMainMenu} from '../../../_shared/GWTBridge/invoke/priApi';
import cx from 'classnames';
import dialogCSS from '../../../features/Automations/common-dialog-styles.module.scss';

export type Props = {
    isOpen: boolean;
    selector: string;
    path: string;
    eName?: string;
};

export const AnalyticsReportsDialog = observer(({selector, path, eName}: Props) => {
    const {isOpen, onClose, selectedReport} = analyticsReportsStore;
    const {isLoading} = useReportQuery(analyticsReportsStore);
    const [isLoadingOnSave, setIsLoadingOnSave] = React.useState(false);

    useEffect(() => {
        analyticsReportsStore.open();
        return () => {
            handleCloseDialog();
        };
    }, []);

    const updateInputFields = (inputFields: EditField[], repExec: number, menuName: string) => {
        const inputFieldsReturn = [
            {field: inputFields[0].field, op: 0, value: menuName},
            {field: inputFields[1].field, op: 0, value: repExec}
        ];
        return {EditFields: inputFieldsReturn};
    };

    const handleCloseDialog = () => {
        onClose();
        document.getElementById(selector)?.remove();
    };

    const footerText = useMemo(
        () =>
            selectedReport?.title
                ? texts.youChose
                      .replace('X', selectedReport.title)
                      .replace('Q', selectedReport.createdDate)
                      .replace('Z', selectedReport.userName)
                : texts.labelSelect,
        [selectedReport?.title]
    );
    const handleAddToMenu = useCallback(async () => {
        setIsLoadingOnSave(true);
        const successfulText = (texts.toastText as string)
            .replace('X', selectedReport.title)
            .replace('Y', path);
        const isOk = await runProcedure(
            ADD_ANALYTICS_TO_MENU,
            updateInputFields,
            selectedReport.id as number,
            eName,
            successfulText
        );
        if (isOk) {
            handleCloseDialog();
            reloadMainMenu();
        }
        setIsLoadingOnSave(false);
    }, [selectedReport?.id, eName, path]);

    if (!isOpen) {
        return null;
    }

    return (
        <HTMLDialog
            isOpen={isOpen}
            onClose={handleCloseDialog}
            id={ANALYTICS_REPORT_DIALOG}
            size="large"
            componentClasses={cx(styles.reportDialog, dialogCSS.component)}
            width={1200}
            toolbarContent={
                <ReportsToolbarContent
                    mainTitle={path}
                    helpText={texts.helpDefinedReport}
                    showSeparator
                />
            }
            content={
                isLoading || isLoadingOnSave ? (
                    <Flex justifyContent="center" componentClasses={styles.loader}>
                        <CircularLoader />
                    </Flex>
                ) : (
                    <ReportsContent />
                )
            }
            footer={
                <ReportsFooter
                    disabled={isLoadingOnSave || !selectedReport?.id}
                    text={footerText}
                    onCancel={handleCloseDialog}
                    onAddedToMenu={handleAddToMenu}
                />
            }
        ></HTMLDialog>
    );
});
